import { ePHTRAbi } from 'abis/ePHTR'
import { NetworkType } from 'abis/set/sets'
import Big from 'big.js'
import { atom } from 'jotai'
import { createAtomsWithQuery } from 'services/query-v2/atomic-factory'
import { DEFAULT_REFETCH_INTERVAL } from 'services/query-v2/refetch-intervals'
import { viemPublicClients } from 'services/wallet/chains'
import { fromBNValue } from 'utils'
import { Address, zeroAddress } from 'viem'
import { DEFAULT_INDEX_DECIMALS } from '../../../../../app-constants/index'
import { QUERY_USER_STAKING_INFO } from '../../../keys'
import { rpcResponseErrorBoundary } from '../services'

const useStakingBalanceQuery = async <T>({
  queryKey: [, userAddress, productAddress],
}: {
  queryKey: [string, Address, Address]
}) => {
  if (productAddress === zeroAddress || userAddress === zeroAddress) {
    return Big(0) as T
  }

  const ephtrContract = {
    address: productAddress,
    abi: ePHTRAbi,
  }

  const [[decimals, balanceOf]] = await Promise.all([
    viemPublicClients[NetworkType.Mainnet].multicall({
      contracts: [
        {
          ...ephtrContract,
          functionName: 'decimals',
        },
        {
          ...ephtrContract,
          functionName: 'balanceOf',
          args: [userAddress],
        },
      ],
    }),
  ])

  rpcResponseErrorBoundary([decimals, balanceOf])

  const userBalance = BigInt(balanceOf.result ?? 0)
  const withdrawableBalance = await viemPublicClients[NetworkType.Mainnet].readContract({
    address: productAddress,
    abi: ePHTRAbi,
    functionName: 'withdrawableAmount',
    args: [userBalance],
  })

  return fromBNValue(withdrawableBalance.toString(), decimals.result ?? DEFAULT_INDEX_DECIMALS) as T
}

export const userStakingBalanceAtomFactory = () => {
  const userAddressAtom = atom<Address>(zeroAddress)
  const productAddressAtom = atom<Address>(zeroAddress)

  const mainProperties = createAtomsWithQuery<
    Big,
    Address,
    Address,
    null,
    [string, Address, Address]
  >(
    { first: userAddressAtom, second: productAddressAtom },
    QUERY_USER_STAKING_INFO,
    useStakingBalanceQuery<Big>,
    DEFAULT_REFETCH_INTERVAL,
  )

  return { userAddress: userAddressAtom, productAddress: productAddressAtom, ...mainProperties }
}
